import { Injectable } from '@angular/core';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { IconSnackBarComponent } from '../components/icon-snack-bar/icon-snack-bar.component';

/**
 * Available icon types for the custom snackbar
 */
export type SnackbarIconType = 'info' | 'warning' | 'success' | 'danger';

/**
 * Configuration options for the custom snackbar
 */
export interface CustomSnackbarConfig extends MatSnackBarConfig {
  message: string;
  icon: SnackbarIconType;
}

/**
 * Service for displaying custom snackbar notifications throughout the application
 */
@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  
  constructor(private snackBar: MatSnackBar) { }

  /**
   * Opens a custom snackbar with an icon
   * @param message The message to display in the snackbar
   * @param icon The icon type to display ('info', 'warning', 'success', 'danger')
   * @param config Optional MatSnackBarConfig options
   */
  public openCustomSnackBar(message: string, icon: SnackbarIconType, config?: MatSnackBarConfig): void {
    const defaultConfig: MatSnackBarConfig = {
      duration: 3000,
    };

    this.snackBar.openFromComponent(IconSnackBarComponent, {
      ...defaultConfig,
      ...config,
      data: {
        message,
        icon,
      },
    });
  }

  /**
   * Convenience method for showing a success message
   * @param message The message to display
   * @param config Optional MatSnackBarConfig options
   */
  public showSuccess(message: string, config?: MatSnackBarConfig): void {
    this.openCustomSnackBar(message, 'success', config);
  }

  /**
   * Convenience method for showing an error message
   * @param message The message to display
   * @param config Optional MatSnackBarConfig options
   */
  public showError(message: string, config?: MatSnackBarConfig): void {
    this.openCustomSnackBar(message, 'danger', config);
  }

  /**
   * Convenience method for showing an info message
   * @param message The message to display
   * @param config Optional MatSnackBarConfig options
   */
  public showInfo(message: string, config?: MatSnackBarConfig): void {
    this.openCustomSnackBar(message, 'info', config);
  }

  /**
   * Convenience method for showing a warning message
   * @param message The message to display
   * @param config Optional MatSnackBarConfig options
   */
  public showWarning(message: string, config?: MatSnackBarConfig): void {
    this.openCustomSnackBar(message, 'warning', config);
  }
}
